import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { analyticsEvents, AnalyticsTimer } from '@/lib/analytics'
import { 
  Presentation,
  ArrowRight, 
  ArrowLeft,
  Loader2,
  Play,
  Pause,
  RotateCcw,
  Brain,
  Bot,
  Workflow,
  BarChart3,
  Users,
  Lightbulb,
  Target,
  Zap,
  Settings,
  CheckCircle,
  Clock,
  Home
} from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'
import { usePresentationStore } from '@/stores/presentationStore'

interface Slide {
  id: number
  title: string
  subtitle?: string
  content: React.ReactNode
  presenterNotes?: string
  duration?: number // minutes
}

const slides: Slide[] = [
  {
    id: 1,
    title: "Welcome to Your AI Journey",
    subtitle: "From Curious to Capable",
    duration: 1,
    content: (
      <div className="text-center space-y-8">
        <div className="mx-auto w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
          <Brain className="w-12 h-12 text-primary" />
        </div>
        <div className="space-y-4">
          <h2 className="text-4xl font-bold">Welcome to Your AI Journey</h2>
          <p className="text-xl text-muted-foreground">From Curious to Capable</p>
          <p className="text-lg max-w-2xl mx-auto">
            In 15 minutes, you'll understand the AI landscape and be ready for hands-on experience 
            that transforms your business approach to automation and intelligence.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "Welcome everyone. Today's session will transform you from AI-curious to AI-capable. We'll spend 15 minutes understanding the landscape, then dive into hands-on experience with real business applications."
  },
  {
    id: 2,
    title: "The Modern Business Owner's Dilemma",
    subtitle: "More Work, Less Time",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">The Modern Business Owner's Dilemma</h2>
          <p className="text-xl text-muted-foreground mt-2">More Work, Less Time</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-red-600">The Capacity Crisis</h3>
            <ul className="space-y-3 text-lg">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                <span><strong>41%</strong> cite rising supply costs</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                <span><strong>40%</strong> can't fill open positions</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                <span><strong>54%</strong> struggle to find customers</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                <span><strong>57%</strong> report extreme stress</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-green-600">The Solution</h3>
            <div className="p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-lg font-medium text-green-700 dark:text-green-400 mb-3">
                Create Capacity Through Intelligence
              </p>
              <p className="text-muted-foreground">
                AI and automation aren't about replacing you—they're about freeing you 
                to focus on strategy, relationships, and growth.
              </p>
            </div>
          </div>
        </div>
        
        <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
          <p className="text-primary font-medium">
            Today, you'll discover how to transform from working <em>in</em> your business to working <em>on</em> your business.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "Ground the presentation in real business pain. These statistics resonate because they reflect the audience's daily reality. Emphasize that technology is the solution to capacity constraints, not an additional burden."
  },
  {
    id: 3,
    title: "The AI Revolution is Here",
    duration: 1,
    content: (
      <div className="space-y-8">
        <h2 className="text-3xl font-bold text-center">The AI Revolution is Here</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">The Numbers Don't Lie</h3>
            <ul className="space-y-3 text-lg">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>ChatGPT reached 100M users in 2 months</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>AI market size: $136B by 2030</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>75% of businesses plan AI adoption</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>40% productivity gains reported</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">The Question Is Not "If"</h3>
            <p className="text-lg text-muted-foreground">
              The question is not whether AI will transform your business. 
              The question is: <strong>Will you be leading the transformation 
              or playing catch-up?</strong>
            </p>
            <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
              <p className="text-primary font-medium">
                Today, you'll gain the knowledge and hands-on experience 
                to lead that transformation.
              </p>
            </div>
          </div>
        </div>
      </div>
    ),
    presenterNotes: "Establish urgency and opportunity. The revolution is happening now—this isn't about future possibilities. Position the audience as early adopters who will have competitive advantage."
  },
  {
    id: 4,
    title: "Demystifying AI: Simple Analogies",
    subtitle: "Making Complex Tech Relatable",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Demystifying AI: Simple Analogies</h2>
          <p className="text-xl text-muted-foreground mt-2">Making Complex Tech Relatable</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold">AI = The Eager Intern</h3>
              </div>
              <p className="text-muted-foreground">
                ChatGPT, Claude, and Gemini are like brilliant interns who need clear instructions 
                to produce their best work. Fast, creative, and eager to help—but you're still the boss.
              </p>
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                <strong>Best for:</strong> Content creation, brainstorming, analysis, problem-solving
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold">AI Learning: Your Business Pattern Detective</h3>
              </div>
              <p className="text-muted-foreground">
                Think of AI like having a brilliant analyst who works 24/7, instantly spotting patterns in your sales data, 
                customer feedback, and market trends that would take you months to discover manually. The result? Smarter 
                decisions about inventory, pricing, and customer outreach based on data, not guesswork.
              </p>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm">
                <strong>Best for:</strong> Predictions, customer insights, market analysis, optimization
              </div>
            </div>
          </Card>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border">
          <p className="text-lg font-medium">
            <strong>Key Insight:</strong> AI isn't magic—it's pattern recognition and content generation you can understand and control.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "Ground AI in familiar concepts. The 'intern' analogy establishes the human-in-control relationship. The 'line of best fit' connects to something they learned in school, making AI learning approachable."
  },
  {
    id: 5,
    title: "Understanding the AI Landscape",
    subtitle: "Not All AI is Created Equal",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Understanding the AI Landscape</h2>
          <p className="text-xl text-muted-foreground mt-2">Not All AI is Created Equal</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                <Brain className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold">AI Tools</h3>
              <p className="text-sm text-muted-foreground">
                Direct interaction tools like ChatGPT, Claude, Gemini. 
                You ask, they respond.
              </p>
              <div className="text-xs text-muted-foreground">
                <strong>Best for:</strong> Content creation, analysis, problem-solving
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <Workflow className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold">Automation</h3>
              <p className="text-sm text-muted-foreground">
                Rule-based systems that handle repetitive tasks. 
                Zapier, IFTTT, workflow builders.
              </p>
              <div className="text-xs text-muted-foreground">
                <strong>Best for:</strong> Repetitive processes, data transfer, scheduling
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                <Bot className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold">AI Agents</h3>
              <p className="text-sm text-muted-foreground">
                Intelligent systems that can plan, execute, and adapt. 
                They work autonomously toward goals.
              </p>
              <div className="text-xs text-muted-foreground">
                <strong>Best for:</strong> Complex workflows, decision-making, learning
              </div>
            </div>
          </Card>
        </div>
        
        <div className="text-center">
          <p className="text-lg text-muted-foreground">
            <strong>Today's Journey:</strong> We'll experience all three, starting with AI tools and building up to agent-like capabilities.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "Key point: Help audience understand these are different categories with different use cases. Most people only know ChatGPT - expand their view."
  },
  {
    id: 6,
    title: "AI Tools: Your New Creative Partner",
    duration: 1,
    content: (
      <div className="space-y-8">
        <h2 className="text-3xl font-bold text-center">AI Tools: Your New Creative Partner</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Brain className="w-6 h-6 text-primary" />
              What Makes Them Powerful
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div>
                  <strong>Instant Expertise:</strong> Access to vast knowledge across domains
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div>
                  <strong>Creative Collaboration:</strong> Brainstorm, refine, and iterate ideas
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Target className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <strong>Personalized Output:</strong> Tailored to your style, audience, and goals
                </div>
              </li>
            </ul>
          </div>
          
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Common Use Cases</h3>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <strong>Content Creation:</strong> Blogs, emails, social media, presentations
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <strong>Analysis & Insights:</strong> Data interpretation, market research, competitive analysis
              </div>
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <strong>Problem Solving:</strong> Strategy development, troubleshooting, decision support
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center p-6 bg-primary/10 rounded-lg border border-primary/20">
          <p className="text-lg font-medium text-primary">
            <strong>Stage 1 & 2:</strong> You'll experience direct AI collaboration through prompting and content creation
          </p>
        </div>
      </div>
    ),
    presenterNotes: "Emphasize collaboration vs replacement. AI tools are creative partners that amplify human capabilities. Set expectations for stages 1-2."
  },
  {
    id: 7,
    title: "Automation: The Digital Assembly Line",
    subtitle: "Your Efficiency Multiplier",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Automation: The Digital Assembly Line</h2>
          <p className="text-xl text-muted-foreground mt-2">Your Efficiency Multiplier</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Workflow className="w-6 h-6 text-green-600" />
              The Assembly Line Analogy
            </h3>
            <p className="text-lg text-muted-foreground">
              Just like a factory assembly line automates physical production, 
              digital automation creates assembly lines for information work.
            </p>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">Real Example:</h4>
              <p className="text-sm text-green-600 dark:text-green-300">
                Contact form → Copy data → Add to spreadsheet → Send to team → Welcome email
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Instead of manual copying/pasting, this happens instantly and perfectly every time.
              </p>
            </div>
          </div>
          
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Why It Matters</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Eliminates manual data entry</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Reduces human error to zero</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Works 24/7 without breaks</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Scales infinitely</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-lg font-medium text-green-700 dark:text-green-400">
            <strong>Stage 3:</strong> You'll build your own automatic tasks using visual, simple tools
          </p>
        </div>
      </div>
    ),
    presenterNotes: "The assembly line analogy makes automation concrete and familiar. Emphasize this is about freeing humans for higher-value work, not replacing them."
  },
  {
    id: 8,
    title: "App Connections: The Universal Waiter",
    subtitle: "How Everything Connects",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">App Connections: The Universal Waiter</h2>
          <p className="text-xl text-muted-foreground mt-2">How Everything Connects</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Users className="w-6 h-6 text-orange-600" />
              The Restaurant Analogy
            </h3>
            <p className="text-lg text-muted-foreground">
              Imagine your business apps are diners at a restaurant. They can't just walk into 
              the kitchen (another app) and grab what they need. That would be chaos!
            </p>
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <h4 className="font-semibold text-orange-700 dark:text-orange-400 mb-2">The App Connection is the Waiter:</h4>
              <ul className="text-sm text-orange-600 dark:text-orange-300 space-y-1">
                <li>• Takes your order (request)</li>
                <li>• Brings it to the kitchen (server)</li>  
                <li>• Delivers exactly what you asked for</li>
                <li>• Keeps everything secure and organized</li>
              </ul>
            </div>
          </div>
          
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Why This Matters</h3>
            <p className="text-muted-foreground mb-4">
              App connections allow your disconnected business tools to work together seamlessly.
            </p>
            <div className="space-y-3">
              <div className="p-3 border rounded-lg">
                <strong className="text-sm">Contact Form ↔ Email Platform</strong>
                <p className="text-xs text-muted-foreground">New subscriber automatically added to email list</p>
              </div>
              <div className="p-3 border rounded-lg">
                <strong className="text-sm">CRM ↔ Accounting Software</strong>
                <p className="text-xs text-muted-foreground">New sale automatically creates invoice</p>
              </div>
              <div className="p-3 border rounded-lg">
                <strong className="text-sm">Calendar ↔ Video Platform</strong>
                <p className="text-xs text-muted-foreground">Meeting scheduled automatically creates Zoom link</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-lg border">
          <p className="text-lg font-medium">
            <strong>Key Takeaway:</strong> App connections are the invisible waiters making your business apps work together like magic.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "The restaurant analogy makes app connections concrete and memorable. Emphasize that app connections enable the automatic tasks they'll build - this connects directly to the hands-on experience."
  },
  {
    id: 9,
    title: "AI Agents: The Future is Autonomous",
    duration: 1,
    content: (
      <div className="space-y-8">
        <h2 className="text-3xl font-bold text-center">AI Agents: The Future is Autonomous</h2>
        
        <div className="space-y-8">
          <div className="text-center">
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              AI Agents represent the next evolution: intelligent systems that can 
              understand goals, make decisions, take actions, and learn from results.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6 text-center">
              <div className="mx-auto w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mb-4">
                <Target className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Goal-Oriented</h3>
              <p className="text-sm text-muted-foreground">
                Give them an objective, and they'll figure out how to achieve it
              </p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                <Brain className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Adaptive</h3>
              <p className="text-sm text-muted-foreground">
                Learn from feedback and improve their approach over time
              </p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                <Workflow className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Multi-Step</h3>
              <p className="text-sm text-muted-foreground">
                Handle complex tasks that require multiple tools and decisions
              </p>
            </Card>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-8 rounded-lg">
            <h3 className="text-xl font-semibold mb-4 text-center">Agent Example: Marketing Campaign Manager</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2">You Say:</h4>
                <p className="text-sm bg-white dark:bg-gray-800 p-3 rounded italic">
                  "Launch a marketing campaign for our new product targeting small business owners"
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Agent Does:</h4>
                <ul className="text-sm space-y-1">
                  <li>• Researches target audience</li>
                  <li>• Creates campaign content</li>
                  <li>• Sets up ad campaigns</li>
                  <li>• Monitors performance</li>
                  <li>• Optimizes based on results</li>
                  <li>• Reports back with insights</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center p-6 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <p className="text-lg font-medium text-purple-700 dark:text-purple-400">
            <strong>Stages 4 & 5:</strong> You'll experience agent-like capabilities through data analysis and personalized recommendations
          </p>
        </div>
      </div>
    ),
    presenterNotes: "This is the 'wow' moment. Help audience understand the leap from tools to autonomous systems. Emphasize that we're building toward this capability."
  },
  {
    id: 10,
    title: "Crawl Phase: Quick Wins in Action",
    subtitle: "AI Assistants for Your Business",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Crawl Phase: Quick Wins in Action</h2>
          <p className="text-xl text-muted-foreground mt-2">AI Assistants for Your Business</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              2-Minute Demo: "Brand Voice Social Writer"
            </h3>
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400 mb-2">For "The Daily Grind" Coffee Shop:</p>
              <ul className="text-sm text-yellow-600 dark:text-yellow-300 space-y-1">
                <li>• "Write Instagram post about Pumpkin Spice Latte"</li>
                <li>• Gets friendly, witty tone automatically</li>
                <li>• Includes call-to-action to visit shop</li>
                <li>• Adds relevant emoji</li>
                <li>• Knows menu, hours, specials</li>
              </ul>
            </div>
            <p className="text-muted-foreground">
              <strong>Result:</strong> A specialized marketing assistant that knows your business, 
              understands your voice, and works 24/7.
            </p>
          </div>
          
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">More Quick Win Examples</h3>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg">
                <strong className="text-blue-600">Customer Service FAQ Bot</strong>
                <p className="text-sm text-muted-foreground">Trained on your common questions and answers</p>
              </div>
              <div className="p-4 border rounded-lg">
                <strong className="text-green-600">Sales Proposal Writer</strong>
                <p className="text-sm text-muted-foreground">Knows your services, pricing, and process</p>
              </div>
              <div className="p-4 border rounded-lg">
                <strong className="text-purple-600">Meeting Summarizer</strong>
                <p className="text-sm text-muted-foreground">Extracts action items in your format</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center p-6 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg border">
          <p className="text-lg font-medium">
            <strong>The Barrier to Entry Has Disappeared:</strong> No coding required—just business knowledge you already have.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "This demonstrates immediate accessibility. The coffee shop example is relatable and shows how anyone can build useful AI tools in minutes. Emphasize the shift from coding barriers to business knowledge as the only requirement."
  },
  {
    id: 11,
    title: "Walk Phase: Automation Success Stories",
    subtitle: "Connecting Your Business Apps",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Walk Phase: Automation Success Stories</h2>
          <p className="text-xl text-muted-foreground mt-2">Connecting Your Business Apps</p>
        </div>
        
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Workflow className="w-5 h-5 text-green-600" />
                The Automated Lead Follow-Up
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>Contact form submitted</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span>Automatically saved to spreadsheet</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-purple-500 rounded-full" />
                  <span>Team notified in Slack</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span>Welcome email sent instantly</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-xs text-green-600 dark:text-green-300">
                  <strong>Before:</strong> 15 minutes of manual work per lead
                  <br />
                  <strong>After:</strong> Instant, perfect execution
                </p>
              </div>
            </Card>
            
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Content Publishing Pipeline  
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>New blog post published</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  <span>Social media posts created & scheduled</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-purple-500 rounded-full" />
                  <span>Newsletter subscribers notified</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span>Analytics tracking updated</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-xs text-blue-600 dark:text-blue-300">
                  <strong>ROI:</strong> 2 hours saved per blog post
                  <br />
                  <strong>Benefit:</strong> Consistent marketing reach
                </p>
              </div>
            </Card>
          </div>
          
          <div className="text-center p-6 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border">
            <h3 className="text-lg font-semibold mb-2">Visual, No-Code Tools Make This Easy</h3>
            <p className="text-muted-foreground">
              Zapier and n8n provide drag-and-drop interfaces. If you can draw a flowchart, you can build automation.
            </p>
          </div>
        </div>
      </div>
    ),
    presenterNotes: "Focus on concrete time savings and ROI. These examples show practical automatic tasks they can implement immediately. Emphasize the visual, simple approach to reduce intimidation."
  },
  {
    id: 12,
    title: "Run Phase: Enterprise-Level Transformation",
    subtitle: "What's Possible at Scale",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Run Phase: Enterprise-Level Transformation</h2>
          <p className="text-xl text-muted-foreground mt-2">What's Possible at Scale</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold">Zip Financial Services</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Tickets/month:</span>
                  <span className="font-medium">2,000+</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Resolution rate:</span>
                  <span className="font-medium text-green-600">93.6%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>ROI:</span>
                  <span className="font-medium text-green-600">473%</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                AI handles routine customer service, humans focus on complex issues
              </p>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold">Global Retail Chain</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Coupon usage:</span>
                  <span className="font-medium text-blue-600">+15%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Campaign cycle:</span>
                  <span className="font-medium">3 months → 1 month</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Method:</span>
                  <span className="font-medium">AI pattern analysis</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                AI analyzes customer behavior to optimize marketing campaigns
              </p>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold">Finexkap Fintech</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Processing speed:</span>
                  <span className="font-medium text-purple-600">7x faster</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Data processing:</span>
                  <span className="font-medium">Fully automated</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Product delivery:</span>
                  <span className="font-medium">Re-engineered</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Complete automatic task systems transformed core business processes
              </p>
            </div>
          </Card>
        </div>
        
        <div className="text-center p-6 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border">
          <h3 className="text-lg font-semibold mb-2">The Strategic Advantage</h3>
          <p className="text-muted-foreground">
            These companies didn't just adopt AI—they built it into everything to create sustainable competitive advantages. 
            <strong> This is where the journey leads.</strong>
          </p>
        </div>
      </div>
    ),
    presenterNotes: "These real case studies with quantified results show the ultimate potential. Emphasize that while we start with simple tools, the journey leads to transformational business impact."
  },
  {
    id: 13,
    title: "The Crawl-Walk-Run Framework",
    subtitle: "Your Implementation Roadmap",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">The Crawl-Walk-Run Framework</h2>
          <p className="text-xl text-muted-foreground mt-2">Your Implementation Roadmap</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-green-700 dark:text-green-400">CRAWL</h3>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-green-600 dark:text-green-300 font-medium">Quick wins with existing tools</p>
                <ul className="text-xs text-green-600 dark:text-green-300 space-y-1">
                  <li>• Build your own AI assistant (5 mins)</li>
                  <li>• Improve prompting techniques</li>
                  <li>• Content creation & analysis</li>
                </ul>
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded text-xs">
                  <strong>Investment:</strong> $20/month
                  <br />
                  <strong>Time:</strong> Hours to implement
                </div>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                  <Workflow className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400">WALK</h3>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-blue-600 dark:text-blue-300 font-medium">Connect apps with simple automatic tasks</p>
                <ul className="text-xs text-blue-600 dark:text-blue-300 space-y-1">
                  <li>• Zapier/n8n automatic tasks</li>
                  <li>• Lead management automation</li>
                  <li>• Content publishing shortcuts</li>
                </ul>
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-xs">
                  <strong>Investment:</strong> $50-200/month
                  <br />
                  <strong>Time:</strong> Days to implement
                </div>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                  <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-purple-700 dark:text-purple-400">RUN</h3>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-purple-600 dark:text-purple-300 font-medium">AI built into everything</p>
                <ul className="text-xs text-purple-600 dark:text-purple-300 space-y-1">
                  <li>• Custom AI solutions</li>
                  <li>• Predictive analytics</li>
                  <li>• Autonomous systems</li>
                </ul>
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded text-xs">
                  <strong>Investment:</strong> $500+ and/or expert help
                  <br />
                  <strong>Time:</strong> Weeks to months
                </div>
              </div>
            </div>
          </Card>
        </div>
        
        <div className="p-6 bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 dark:from-green-900/20 dark:via-blue-900/20 dark:to-purple-900/20 rounded-lg border">
          <h3 className="text-lg font-semibold mb-2 text-center">Common Business Pain Points → Solutions</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Inconsistent Marketing:</strong> CRAWL → Custom Brand Voice GPT
            </div>
            <div>
              <strong>Leads Falling Through Cracks:</strong> WALK → Automatic lead management
            </div>
            <div>
              <strong>Repetitive Customer Questions:</strong> CRAWL → FAQ AI assistant
            </div>
            <div>
              <strong>Manual Admin Tasks:</strong> WALK → Document automation
            </div>
          </div>
        </div>
      </div>
    ),
    presenterNotes: "This framework gives them a clear progression with realistic expectations for investment and timeline. Map specific business problems to solutions to make it actionable."
  },
  {
    id: 14,
    title: "Your Journey Roadmap",
    subtitle: "What You'll Experience Today",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Your Journey Roadmap</h2>
          <p className="text-xl text-muted-foreground mt-2">What You'll Experience Today</p>
        </div>
        
        <div className="space-y-6">
          <div className="grid md:grid-cols-5 gap-4">
            <Card className="p-4 text-center bg-primary/5 border-primary/20">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                <Brain className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Stage 1</h3>
              <p className="text-xs text-muted-foreground">AI Conversation</p>
              <p className="text-xs mt-2">Learn effective prompting</p>
            </Card>
            
            <Card className="p-4 text-center">
              <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Stage 2</h3>
              <p className="text-xs text-muted-foreground">Creative Partnership</p>
              <p className="text-xs mt-2">Collaborate on content</p>
            </Card>
            
            <Card className="p-4 text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-3">
                <Workflow className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Stage 3</h3>
              <p className="text-xs text-muted-foreground">Process Automation</p>
              <p className="text-xs mt-2">Build automatic tasks</p>
            </Card>
            
            <Card className="p-4 text-center">
              <div className="mx-auto w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mb-3">
                <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Stage 4</h3>
              <p className="text-xs text-muted-foreground">Business Intelligence</p>
              <p className="text-xs mt-2">Analyze data</p>
            </Card>
            
            <Card className="p-4 text-center">
              <div className="mx-auto w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mb-3">
                <Target className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="font-semibold text-sm mb-1">Stage 5</h3>
              <p className="text-xs text-muted-foreground">Personal Roadmap</p>
              <p className="text-xs mt-2">Custom strategy</p>
            </Card>
          </div>
          
          <div className="bg-gradient-to-r from-primary/10 to-blue-50 dark:to-blue-900/20 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-4 text-center">The Progressive Learning Approach</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  Learn
                </h4>
                <p className="text-sm text-muted-foreground">
                  Each stage builds on the previous, introducing new concepts and capabilities
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-500" />
                  Apply
                </h4>
                <p className="text-sm text-muted-foreground">
                  Hands-on practice with real tools and immediate feedback
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <p className="text-lg font-medium">
            By the end, you'll have experienced the full spectrum of AI capabilities 
            and have a personalized roadmap for implementation.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "Build excitement for the journey ahead. Emphasize progression and practical outcomes. This is the bridge from education to hands-on experience."
  },
  {
    id: 15,
    title: "Interactive Myth-Busting Game",
    subtitle: "Two Truths and a Lie",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">Interactive Myth-Busting Game</h2>
          <p className="text-xl text-muted-foreground mt-2">Two Truths and a Lie</p>
        </div>
        
        <div className="space-y-8">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">
              Let's test your new AI knowledge! Which of these statements is <strong>FALSE</strong>?
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6 border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-red-600 dark:text-red-400">A</span>
                  </div>
                  <h3 className="text-lg font-semibold">The Lie!</h3>
                </div>
                <p className="text-sm">
                  "To build a useful, custom AI tool for your business, you need to be an expert coder or hire a developer."
                </p>
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <p className="text-xs text-red-600 dark:text-red-300">
                    <strong>Why it's false:</strong> AI assistants and simple tools mean you only need business knowledge—which you already have!
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-green-600 dark:text-green-400">B</span>
                  </div>
                  <h3 className="text-lg font-semibold">Truth</h3>
                </div>
                <p className="text-sm">
                  "You can build a specialized AI assistant that is trained on your own business data in just a few minutes using AI assistant builders."
                </p>
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <p className="text-xs text-green-600 dark:text-green-300">
                    <strong>Absolutely true:</strong> We saw this in the coffee shop demo—just upload your info and start using it!
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-green-600 dark:text-green-400">C</span>
                  </div>
                  <h3 className="text-lg font-semibold">Truth</h3>
                </div>
                <p className="text-sm">
                  "AI tools can automate multi-step processes, like lead follow-up, by connecting different software applications you already use."
                </p>
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <p className="text-xs text-green-600 dark:text-green-300">
                    <strong>Absolutely true:</strong> Zapier and n8n make this possible with visual, drag-and-drop interfaces.
                  </p>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="text-center p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg border">
            <p className="text-lg font-medium">
              <strong>Key Insight:</strong> The biggest myth is that AI is too complex or expensive to start with. 
              The reality? You can begin transforming your business today with tools you can learn in minutes.
            </p>
          </div>
        </div>
      </div>
    ),
    presenterNotes: "This interactive game reinforces key learning points while building confidence. Emphasize that accessibility is real—the barriers have truly fallen for business owners willing to learn."
  },
  {
    id: 16,
    title: "How This Presentation Was Built",
    subtitle: "AI Building AI Tools",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold">How This Presentation Was Built</h2>
          <p className="text-xl text-muted-foreground mt-2">AI Building AI Tools</p>
        </div>
        
        <div className="space-y-8">
          <div className="text-center">
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              This presentation itself demonstrates the power of AI collaboration. 
              Here's the actual process used to create what you've just experienced.
            </p>
          </div>
          
          <div className="relative">
            {/* Workflow visualization */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="p-6 text-center bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                  <Brain className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">1. ChatGPT</h3>
                <p className="text-sm text-muted-foreground mb-3">Initial Research & Strategy</p>
                <ul className="text-xs text-blue-600 dark:text-blue-300 space-y-1">
                  <li>• Content brainstorming</li>
                  <li>• Structure planning</li>
                  <li>• Audience analysis</li>
                </ul>
              </Card>
              
              <Card className="p-6 text-center bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                <div className="mx-auto w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">2. Research Agent</h3>
                <p className="text-sm text-muted-foreground mb-3">Deep Content Analysis</p>
                <ul className="text-xs text-purple-600 dark:text-purple-300 space-y-1">
                  <li>• Industry statistics</li>
                  <li>• Case study research</li>
                  <li>• Best practices</li>
                </ul>
              </Card>
              
              <Card className="p-6 text-center bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                  <Bot className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">3. Claude Code</h3>
                <p className="text-sm text-muted-foreground mb-3">Interactive Development</p>
                <ul className="text-xs text-green-600 dark:text-green-300 space-y-1">
                  <li>• React components</li>
                  <li>• Interactive features</li>
                  <li>• UI/UX design</li>
                </ul>
              </Card>
              
              <Card className="p-6 text-center bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
                <div className="mx-auto w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mb-4">
                  <Workflow className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">4. MCPs</h3>
                <p className="text-sm text-muted-foreground mb-3">Advanced Integration</p>
                <ul className="text-xs text-orange-600 dark:text-orange-300 space-y-1">
                  <li>• Live documentation</li>
                  <li>• Connects to your data</li>
                  <li>• Browser automation</li>
                </ul>
              </Card>
            </div>
            
            {/* Connecting arrows */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-blue-300 via-purple-300 via-green-300 to-orange-300 transform -translate-y-1/2 z-0" />
            <div className="hidden md:flex absolute top-1/2 transform -translate-y-1/2 w-full justify-between px-8 z-10">
              <ArrowRight className="w-6 h-6 text-blue-500 bg-white dark:bg-gray-900 rounded-full p-1" />
              <ArrowRight className="w-6 h-6 text-purple-500 bg-white dark:bg-gray-900 rounded-full p-1" />
              <ArrowRight className="w-6 h-6 text-green-500 bg-white dark:bg-gray-900 rounded-full p-1" />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                What This Demonstrates
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>AI Collaboration:</strong> Multiple AI tools working together seamlessly</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Rapid Development:</strong> Complex interactive presentation built in hours, not weeks</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                  <span><strong>Human + AI:</strong> Human creativity and strategy combined with AI execution</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Real Results</h3>
              <div className="space-y-3">
                <div className="p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Development Time:</span>
                    <span className="text-lg font-bold text-green-600">6 hours</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Traditional development: 40+ hours</p>
                </div>
                <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Features Built:</span>
                    <span className="text-lg font-bold text-purple-600">25+</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Interactive slides, progress tracking, responsive design</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center p-6 bg-gradient-to-r from-blue-50 via-purple-50 to-green-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-green-900/20 rounded-lg border">
          <p className="text-lg font-medium">
            <strong>This is the future you're stepping into:</strong> AI tools that amplify human creativity and dramatically accelerate execution.
          </p>
        </div>
      </div>
    ),
    presenterNotes: "This slide is a powerful demonstration of AI capabilities in action. It shows the audience exactly how the presentation they just experienced was created using AI collaboration. Emphasize the speed, quality, and complexity achieved through human-AI partnership."
  },
  {
    id: 17,
    title: "Your First Step Challenge",
    subtitle: "Ready to Transform Your Business?",
    duration: 1,
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-4xl font-bold">Your First Step Challenge</h2>
          <p className="text-xl text-muted-foreground mt-2">Ready to Transform Your Business?</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Target className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-semibold">The Challenge</h3>
            </div>
            <div className="p-6 bg-primary/10 rounded-lg border border-primary/20">
              <p className="text-lg font-medium text-primary mb-3">
                Before the end of this week:
              </p>
              <ol className="text-sm space-y-2">
                <li><strong>1.</strong> Identify one small, repetitive task you dislike doing</li>
                <li><strong>2.</strong> Dedicate 10 minutes to build an AI assistant for that task</li>
                <li><strong>3.</strong> Use it to reclaim minutes of your day</li>
              </ol>
            </div>
            <p className="text-muted-foreground text-center">
              <em>This isn't about revolutionizing everything overnight. 
              It's about taking one small step to create capacity.</em>
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                <Lightbulb className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold">Examples to Inspire You</h3>
            </div>
            <div className="space-y-3">
              <div className="p-3 border rounded-lg">
                <strong className="text-sm">Email Response Helper</strong>
                <p className="text-xs text-muted-foreground">For those recurring customer inquiries</p>
              </div>
              <div className="p-3 border rounded-lg">
                <strong className="text-sm">Social Media Post Writer</strong>
                <p className="text-xs text-muted-foreground">In your brand voice, every time</p>
              </div>
              <div className="p-3 border rounded-lg">
                <strong className="text-sm">Meeting Notes Summarizer</strong>
                <p className="text-xs text-muted-foreground">Extract action items automatically</p>
              </div>
              <div className="p-3 border rounded-lg">
                <strong className="text-sm">Proposal Section Writer</strong>
                <p className="text-xs text-muted-foreground">Standard sections, customized content</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center space-y-6">
          <div className="p-6 bg-gradient-to-r from-primary/10 to-green-50 dark:to-green-900/20 rounded-lg border">
            <h3 className="text-xl font-semibold mb-3">The Future of Business</h3>
            <p className="text-lg text-muted-foreground">
              The future isn't about every company becoming a tech company. 
              It's about every company becoming a <strong>smart company</strong>.
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="text-lg font-medium">
              <strong>Next:</strong> Stage 1 - Your First AI Conversation
            </div>
            <p className="text-muted-foreground">
              Now let's dive into hands-on experience that transforms you from AI-aware to AI-capable.
            </p>
          </div>
        </div>
      </div>
    ),
    presenterNotes: "End with specific, actionable challenge that breaks down barriers to getting started. The one-week timeframe creates urgency. Transition confidently to hands-on experience—they're now equipped with the foundation they need."
  }
]

export function Stage0Presentation() {
  const navigate = useNavigate()
  const { completeStage, updateStageProgress, user } = useJourneyStore()
  const { isPresenterMode, currentSession, liveParticipants } = usePresentationStore()
  const location = useLocation()
  
  // Check if this is standalone mode (accessed via /overview)
  const isStandaloneMode = location.pathname === '/overview'
  
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPresenting, setIsPresenting] = useState(false)
  const [presentationStartTime, setPresentationStartTime] = useState<Date | null>(null)
  const [slideStartTime, setSlideStartTime] = useState<Date | null>(null)
  const [slideTimings, setSlideTimings] = useState<Record<number, number>>({})
  const [isCompleting, setIsCompleting] = useState(false)
  const [completionMessage, setCompletionMessage] = useState('')
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false)
  const [showContinuationOptions, setShowContinuationOptions] = useState(false)
  
  const totalSlides = slides.length
  const currentSlideData = slides[currentSlide]

  // Helper function to get slide titles for analytics
  const getSlideTitle = (slideIndex: number) => {
    if (slides[slideIndex] && slides[slideIndex].title) {
      return slides[slideIndex].title
    }
    return `Slide ${slideIndex + 1}`
  }
  
  useEffect(() => {
    if (isPresenting && slideStartTime) {
      const timer = setInterval(() => {
        const now = new Date()
        const timeSpent = Math.round((now.getTime() - slideStartTime.getTime()) / 1000)
        setSlideTimings(prev => ({ ...prev, [currentSlide]: timeSpent }))
      }, 1000)
      
      return () => clearInterval(timer)
    }
  }, [isPresenting, slideStartTime, currentSlide])
  
  // Keyboard navigation for presentation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isPresenting) return
      
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          if (currentSlide > 0) {
            prevSlide()
          }
          break
        case 'ArrowRight':
          event.preventDefault()
          if (currentSlide < totalSlides - 1) {
            nextSlide()
          }
          break
        case 'Escape':
          event.preventDefault()
          resetPresentation()
          break
        default:
          break
      }
    }

    if (isPresenting) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isPresenting, currentSlide, totalSlides])
  
  const startPresentation = () => {
    setIsPresenting(true)
    setPresentationStartTime(new Date())
    setSlideStartTime(new Date())
    
    // Track presentation start
    analyticsEvents.presentationStarted()
  }
  
  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      // Track current slide before moving to next
      if (slideStartTime) {
        const slideTime = Math.round((new Date().getTime() - slideStartTime.getTime()) / 1000)
        analyticsEvents.presentationSlideViewed(currentSlide, getSlideTitle(currentSlide))
      }
      
      setCurrentSlide(prev => prev + 1)
      setSlideStartTime(new Date())
    }
  }
  
  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1)
      setSlideStartTime(new Date())
    }
  }
  
  const goToSlide = (slideIndex: number) => {
    setCurrentSlide(slideIndex)
    setSlideStartTime(new Date())
  }
  
  const getTotalPresentationTime = () => {
    if (!presentationStartTime) return 0
    return Math.round((new Date().getTime() - presentationStartTime.getTime()) / 1000 / 60)
  }
  
  const resetPresentation = () => {
    setCurrentSlide(0)
    setIsPresenting(false)
    setPresentationStartTime(null)
    setSlideStartTime(null)
    setSlideTimings({})
  }
  
  const handleComplete = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Completing presentation stage...')
    
    try {
      const totalTime = presentationStartTime ? 
        Math.round((new Date().getTime() - presentationStartTime.getTime()) / 1000) : 0
      
      // Track presentation completion
      analyticsEvents.presentationCompleted(totalSlides, totalTime)
      
      const presentationData = {
        slidesViewed: Object.keys(slideTimings).length,
        totalPresentationTime: getTotalPresentationTime(),
        slideTimings,
        completed: true
      }
      
      // Only complete stage if user is authenticated
      if (user) {
        completeStage(0, presentationData)
        setCompletionMessage('Presentation completed! Moving to hands-on experience...')
        // Let the store's auto-advancement handle navigation to Stage 1
      } else {
        // Standalone mode - show continuation options
        setCompletionMessage('Presentation completed! What would you like to do next?')
        setIsCompleting(false)
        setShowContinuationOptions(true)
      }
    } catch (error) {
      console.error('Error completing stage:', error)
      setCompletionMessage('Error completing stage. Please try again.')
      setIsCompleting(false)
    }
  }
  
  const handleSkip = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage(user ? 'Skipping to hands-on experience...' : 'Returning to home...')
    
    try {
      // Save presentation data even when skipping
      const presentationData = {
        skipped: true,
        slidesViewed: Object.keys(slideTimings).length,
        totalPresentationTime: getTotalPresentationTime()
      }
      
      if (user) {
        // If user exists, complete the stage and let store handle navigation
        completeStage(0, presentationData)
      } else {
        // If no user, just navigate back to landing/home
        setTimeout(() => {
          navigate('/')
        }, 1000)
      }
    } catch (error) {
      console.error('Error returning to dashboard:', error)
      setCompletionMessage('Error returning to dashboard. Please try again.')
      setIsCompleting(false)
    }
  }
  
  const canComplete = isPresenting && Object.keys(slideTimings).length >= 3
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      {/* Presentation Header */}
      <div className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Presentation className="w-6 h-6 text-primary" />
                <span className="font-semibold">Stage 0: AI Landscape Overview</span>
              </div>
              {isPresenting && (
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Slide {currentSlide + 1} of {totalSlides}</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {getTotalPresentationTime()}m
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(user ? '/' : '/landing')}
                className="gap-2"
              >
                <Home className="w-4 h-4" />
                {user ? 'Dashboard' : 'Home'}
              </Button>
              
              {isPresenting && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetPresentation}
                    className="gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={prevSlide}
                    disabled={currentSlide === 0}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={nextSlide}
                    disabled={currentSlide === totalSlides - 1}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        {!isPresenting ? (
          /* Pre-Presentation Setup */
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-6">
              <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                <Presentation className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h1 className="text-4xl font-bold">AI Landscape Overview</h1>
                <p className="text-xl text-muted-foreground mt-2">
                  15-minute educational foundation with real-world examples
                </p>
              </div>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Enhanced Presentation Overview</CardTitle>
                <CardDescription>
                  Comprehensive introduction to AI, automation, and business transformation with practical examples
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-3">What You'll Learn</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>The modern business capacity crisis and AI solutions</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Simple analogies that demystify complex AI concepts</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Real success stories with quantified ROI examples</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>The Crawl-Walk-Run implementation framework</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Interactive myth-busting and actionable first steps</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3">Enhanced Content</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Slides:</span>
                        <span className="font-medium">{totalSlides} (was 8)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Estimated Time:</span>
                        <span className="font-medium">15 minutes</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">New Features:</span>
                        <span className="font-medium">Real case studies</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Interactivity:</span>
                        <span className="font-medium">Games & challenges</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Business Focus:</span>
                        <span className="font-medium">ROI & practical examples</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border">
                  <h3 className="font-semibold mb-2">What's New in This Extended Version</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>Real Business Context:</strong> Actual statistics on small business challenges
                    </div>
                    <div>
                      <strong>Powerful Analogies:</strong> "Eager Intern," "Digital Assembly Line," "Universal Waiter"
                    </div>
                    <div>
                      <strong>Success Stories:</strong> Zip (473% ROI), retail chain (+15% performance)
                    </div>
                    <div>
                      <strong>Implementation Roadmap:</strong> Clear Crawl-Walk-Run progression with costs/timing
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-center pt-4">
                  <Button onClick={startPresentation} size="lg" className="gap-2">
                    <Play className="w-5 h-5" />
                    Start Enhanced Presentation
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Main Presentation */
          <div className="border-2 border-primary/20 rounded-lg shadow-lg p-6 bg-background/95 backdrop-blur-sm">
            <div className="space-y-8">
              {/* Slide Content */}
              <Card className="min-h-[600px]">
                <CardContent className="p-12">
                  {currentSlideData.content}
                </CardContent>
              </Card>
            
            {/* Presentation Controls */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Slide Navigation */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Slide Navigation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-2">
                    {slides.map((slide, index) => (
                      <Button
                        key={slide.id}
                        variant={index === currentSlide ? "default" : "outline"}
                        size="sm"
                        onClick={() => goToSlide(index)}
                        className="text-xs p-2 h-auto"
                      >
                        {index + 1}
                      </Button>
                    ))}
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Current Slide:</span>
                      <span className="font-medium">{currentSlide + 1} of {totalSlides}</span>
                    </div>
                    {currentSlideData.duration && (
                      <div className="flex justify-between text-sm">
                        <span>Recommended Time:</span>
                        <span className="font-medium">{currentSlideData.duration}m</span>
                      </div>
                    )}
                    {slideTimings[currentSlide] && (
                      <div className="flex justify-between text-sm">
                        <span>Time on Slide:</span>
                        <span className="font-medium">{Math.floor(slideTimings[currentSlide] / 60)}:{(slideTimings[currentSlide] % 60).toString().padStart(2, '0')}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Presenter Notes */}
              {currentSlideData.presenterNotes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Presenter Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {currentSlideData.presenterNotes}
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {/* Stage Completion */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Progress</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-medium">
                          Slides viewed: {Object.keys(slideTimings).length}/{totalSlides}
                        </span>
                        {canComplete && <CheckCircle className="w-4 h-4 text-green-600" />}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Total time: {getTotalPresentationTime()} minutes
                      </div>
                    </div>
                    
                    {completionMessage && (
                      <div className="text-center p-2 bg-primary/10 text-primary rounded-md text-sm">
                        {completionMessage}
                      </div>
                    )}
                    
                    {showContinuationOptions && (
                      <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border">
                        <h3 className="text-lg font-semibold text-center">What's Next?</h3>
                        <div className="grid gap-3">
                          <Button
                            onClick={() => navigate('/onboarding')}
                            className="gap-2 w-full"
                          >
                            <Play className="w-4 h-4" />
                            Start Full AI Journey
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate('/landing')}
                            className="gap-2 w-full"
                          >
                            <Home className="w-4 h-4" />
                            Back to Home
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      {canComplete && (
                        <Button
                          onClick={handleComplete}
                          disabled={isCompleting}
                          className="w-full gap-2"
                        >
                          {isCompleting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>Complete Presentation <ArrowRight className="w-4 h-4" /></>
                          )}
                        </Button>
                      )}
                      
                      <Button
                        onClick={handleSkip}
                        variant="outline"
                        disabled={isCompleting}
                        className="w-full gap-2"
                      >
                        {isCompleting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>{user ? 'Skip to Hands-On Experience' : 'Return to Home'} <ArrowRight className="w-4 h-4" /></>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          </div>
        )}
      </div>
    </div>
  )
}